'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { ENGINE_PATH_PREFIX } from '@gnsx/genesys.js';
import { invalidate } from '@react-three/fiber';
import { camelCase, pascalCase, throttle } from 'es-toolkit';
import { flow, getRoot, isAlive, t } from 'mobx-state-tree';
import * as THREE from 'three';

import { buildObjectTree } from './buildObjectTree';
import { ObjectTree } from './objectTree';

import type { IEditorStore } from '@/editor/store/editorStore';
import type { ISceneStore } from '@/editor/store/sceneStore';
import type { Instance } from 'mobx-state-tree';

import { nameHelpers, objectActions, objectHelpers, prefabActions } from '@/editor/core';
import { notifyChanged } from '@/editor/core/actions/editorActions';
import { reloadActorDeferred } from '@/editor/core/actions/sceneActions';
import { type IPropertyStore, PropertyStore } from '@/editor/property-editor/propertyStore';
import { editorStore } from '@/editor/store/editorStore';

const DEBUG_LOG = { init: false, hierarchy: false, reloadActor: true } as const;

export const EditableObject = t
  .model('EditableObject', {
    id: t.identifier,
    name: t.string,
    hierarchy: t.optional(t.late(() => ObjectTree), () => ObjectTree.create({ nodes: [] })), // ObjectTree
    propertyStore: t.safeReference(PropertyStore),
    isSharedStore: t.optional(t.boolean, false), // Store shared between multiple objects TODO: possibly remove this?
    isReloading: t.optional(t.boolean, false),
    isInWorldScene: t.optional(t.boolean, false),
  })
  .volatile(() => ({
    _actor: undefined as ENGINE.Actor | undefined,
    _sceneObject: undefined as THREE.Object3D | undefined,
    _worldTarget: undefined as ENGINE.World | undefined,
    _reloadTimer: null as any,
    updateCounter: 0,
  }))
  .views(self => ({
    /* Key for triggering re-renders */
    get idKey() { return `${self.id}-${self.updateCounter}`; },
    get asActor() { return self._actor; },
    get asSceneObject() { return self._sceneObject; },
    get selectedTransformTarget() {
      const selectedSubobject = self.hierarchy.selectedNode?.target;
      if (selectedSubobject instanceof ENGINE.Actor) return self._sceneObject;
      if (selectedSubobject instanceof THREE.Object3D) return selectedSubobject;
      return null;
    },
    get selectedSubobject() { return self.hierarchy.selectedNode; },
    get selectedSubobjects() { return self.hierarchy.selectedNodes; },
    get selectedSceneObjects() { return self.hierarchy.selectedSceneObjects; },
    /** Can be an actor, scene component, component, or world */
    get selectedSubobjectTarget() {
      // For world objects, return the world directly
      if (self.isInWorldScene && self._worldTarget) {
        return self._worldTarget;
      }
      return self.hierarchy.selectedNode?.target;
    },
    get selectedSubobjectName() { return self.hierarchy.selectedNode?.name ?? ''; },
    get selectedSubobjectId() { return self.hierarchy.selectedNode?.id ?? ''; },
    // TODO: <AY> Remove read-only restriction for prefab instances once prefab variants/overrides are implemented.
    get isReadOnly() { return this.isEngineAsset || this.isPrefabInstance; },
    get isPrefabInstance() { return objectHelpers.isPrefabInstance(this.asActor); },
    get isPrefabAsset() { return objectHelpers.isPrefabAsset(this.asActor); },
    get isEngineAsset() { return this.prefabName?.startsWith(ENGINE_PATH_PREFIX); },
    get isPrefab() { return this.isPrefabAsset || this.isPrefabInstance; },
    get prefabLabel(): string | undefined { return objectHelpers.getPrefabLabel(this.asActor); },
    get prefabName(): string | undefined { return objectHelpers.getPrefabName(this.asActor); },
    get prefabPath(): string | undefined { return objectHelpers.getPrefabName(this.asActor); },
    getNameById(id: string) { return self.hierarchy.getNodeById(id)?.name; },
    get editorStore(): IEditorStore { return getRoot(self) as IEditorStore; },
    get editorScene(): ISceneStore | undefined { return self.isInWorldScene ? this.editorStore.scene : undefined; },
    getDebugName({ compact, subobjectOnly }: { compact?: boolean, subobjectOnly?: boolean } = { compact: true, subobjectOnly: false }) {
      const subobjectName = this.selectedSubobject?.asActor ? 'actor' : (compact ? camelCase(this.selectedSubobjectName) : this.selectedSubobjectName);
      const name = subobjectOnly ? subobjectName : `${compact ? pascalCase(self.name) : self.name}.${subobjectName}`;
      return `(${name})`;
    }
  }))
  .actions(self => ({
    init(target: ENGINE.Actor, reset: boolean = true) {
      if (DEBUG_LOG.init) console.log('[EditableObject] init', { target: target.uuid, old: self._actor?.uuid, reset });

      // Check if already initialized with same actor
      if (!reset && self._actor?.uuid === target.uuid) {
        return; // Skip rebuild
      }

      self._actor = objectHelpers.findActorFromTarget(target) ?? undefined;
      self._sceneObject = self._actor?.rootComponent ?? undefined;
      self.isInWorldScene = !!self._actor?.getWorld();
      if (self._actor) {
        self.name = nameHelpers.getActorDisplayName(self._actor);
      }

      if (reset) {
        self.hierarchy.reset();
        buildObjectTree(
          (node, parentId) => self.hierarchy.addNode(node, parentId),
          self.id, self.name, self._actor, self._actor?.rootComponent, DEBUG_LOG.hierarchy
        );
        self.hierarchy.selectNode(self.id, false);
      }
    },
    /**
     * Rebuild the component hierarchy from the current engine state while
     * preserving selection and expansion. Use after engine-level reparent
     * operations so the MST tree (and therefore the component tree UI)
     * reflects the new structure without requiring a reselect.
     */
    rebuildHierarchy() {
      const actor = self._actor;
      if (!actor) return;
      self.name = nameHelpers.getActorDisplayName(actor);

      const selectedIds = self.hierarchy.selectedNodeIds;
      const expandedIds = self.hierarchy.expandedNodeIds;

      self.hierarchy.reset();
      buildObjectTree(
        (node, parentId) => self.hierarchy.addNode(node, parentId),
        self.id, self.name, actor, actor.rootComponent, DEBUG_LOG.hierarchy,
      );

      // Restore expansion state
      self.hierarchy.setExpandedNodesIds(expandedIds);

      // Restore selection — fall back to root if the previously-selected node no longer exists
      const validSelectedIds = selectedIds.filter(id => !!self.hierarchy.getNodeById(id));
      if (validSelectedIds.length > 0) {
        self.hierarchy.setSelectedNodeIds(validSelectedIds);
      } else {
        self.hierarchy.selectNode(self.id, false);
      }

      // Bump version so the MobX observer in ComponentTreeView re-renders and
      // headless-tree picks up the new data via rebuildTree (effect + version bump).
      self.hierarchy.bumpVersion();
    },
    initWithWorld(world: ENGINE.World) {
      if (DEBUG_LOG.init) console.log('[EditableObject] initWithWorld', { worldUuid: world.uuid, sceneUuid: world.scene.uuid });
      self._actor = undefined;
      self._sceneObject = world.scene;
      self.isInWorldScene = true;

      // For world objects, we store the world directly and skip hierarchy
      self._worldTarget = world;

      // Reset hierarchy - we don't need complex hierarchy for world
      self.hierarchy.reset();
    },
    setPropertyStore(propertyStore: IPropertyStore | undefined, isSharedStore: boolean) {
      self.propertyStore = propertyStore;
      self.isSharedStore = isSharedStore;
    },
    setName(name: string) {
      self.name = name;
      self.hierarchy.rootNode?.setName(name); // Update component tree
      if (self.asActor) nameHelpers.setCustomDisplayName(self.asActor, name);
    },
    bumpUpdateCounter() {
      self.updateCounter += 1;
    },
    setUIPropertyValue(path: string, value: any) {
      if (!self.isSharedStore && self.propertyStore) {
        // console.log('setUIPropertyValue', path, value, self.propertyStore?.properties);
        self.propertyStore.setPropertyValue(path, value, false, false);
      }
    },
    setVisibilityInEditor(visible: boolean) {
      self.editorScene?.outliner.getNodeById(self.id)?.setVisibilityInEditor(visible);
    },
    toggleVisibilityInEditor() {
      self.editorScene?.outliner.getNodeById(self.id)?.toggleVisibilityInEditor();
    },
    setLockSelectionInEditor(locked: boolean) {
      self.editorScene?.outliner.getNodeById(self.id)?.setLocked(locked);
    },
    toggleLockSelectionInEditor() {
      self.editorScene?.outliner.getNodeById(self.id)?.toggleLocked();
    },
    setIsReloading(value: boolean) {
      self.isReloading = value;
    },
    reloadActor() {
      const actor = self.asActor;
      if (!actor) return;

      const throttledReload = throttle(() => {
        if (DEBUG_LOG.reloadActor) console.log('reloadActor', { name: self.name }, self.asActor);
        const actor = self.asActor;
        if (actor && isAlive(self)) reloadActorDeferred(actor);
      }, 100);

      this.setIsReloading(true);
      throttledReload();

      clearTimeout(self._reloadTimer);
      self._reloadTimer = setTimeout(() => this.setIsReloading(false), 150);
    }
  }))
  .actions(self => ({
    notifyPropertyChanged: flow(function* _notifyPropertyChanged(key: string, value: any, target: any) {
      // console.log('[EditableObject:notifyPropertyChanged] isPrefabAsset', self.isPrefabAsset, self.asActor);
      const result = objectActions.notifyEditorPropertyChanged(target, key, value);
      if (self.isPrefabAsset && self.asActor && !editorStore.undoRedoManager.isReplayingHistory()) {
        prefabActions.markPrefabDirty(objectHelpers.getPrefabName(self.asActor) ?? undefined);
      }
      // Keep MST name and hierarchy in sync when the 'name' property is changed via the property panel
      if (key === 'name' && typeof value === 'string') {
        self.name = value;
        self.hierarchy.rootNode?.setName(value);
      }
      invalidate(); // Invalidates render view, only really needed for render related changes...
      notifyChanged('SetValue', { name: self.name, key, value });
    }),
    afterDestroy() {
      self._worldTarget = undefined;
      self._sceneObject = undefined;
      self._actor = undefined;
    }
  }))
  .actions(self => {
    const withIsAlive = (fn: (value: THREE.Vector3) => void) => throttle((value: THREE.Vector3) => isAlive(self) ? fn(value) : null, 33);
    const throttledSetDisplayPosition = withIsAlive((value: THREE.Vector3) => self.setUIPropertyValue('position', value));
    const throttledSetDisplayRotation = withIsAlive((value: THREE.Vector3) => self.setUIPropertyValue('rotation', value));
    const throttledSetDisplayScale = withIsAlive((value: THREE.Vector3) => self.setUIPropertyValue('scale', value));
    return {
      setDisplayPosition(vector: THREE.Vector3) {
        throttledSetDisplayPosition(vector);
      },
      setDisplayRotation(vector: THREE.Vector3) {
        throttledSetDisplayRotation(vector);
      },
      setDisplayScale(vector: THREE.Vector3) {
        throttledSetDisplayScale(vector);
      },
    };
  });

export interface IEditableObject extends Instance<typeof EditableObject> { }
